import { useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { RcFile } from 'antd/es/upload';
import {
  CommentAddRequestBody,
  CommentGetAllVariables,
  fetchCommentGetAll,
  OpenapiContentType,
  useAddComment,
} from '@/shared/api';
import { ChatMode } from '@/shared/types';
import { captureError } from '@/shared/utils';
import { agentConversationHistoryResponseToMessages } from '../api/mappers';
import { MAX_CHAT_HISTORY_LENGTH } from '../config/chat';
import { ChatMessageType } from '../model/types';

type UseCommentsProps = {
  instanceId?: string;
  stepId: string;
  queryKey: string;
  postedBy: CommentAddRequestBody['postedBy'];
  chatOpen?: boolean;
  chatMode?: ChatMode;
  accessToken?: string;
  code?: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useInstanceAgentConversation = ({
  instanceId = '',
  stepId,
  queryKey,
  postedBy,
  chatOpen,
  chatMode,
  accessToken,
  code,
  setIsLoading,
}: UseCommentsProps) => {
  const { mutate: addComment } = useAddComment();

  const fetchAgentMessages = async ({ pageParam = 1 }) => {
    let requestOptions: CommentGetAllVariables = {
      pathParams: { instanceId, stepId },
      queryParams: {
        order: 'DESC',
        take: MAX_CHAT_HISTORY_LENGTH,
        page: pageParam,
      },
    };

    if (accessToken) {
      requestOptions = {
        ...requestOptions,
        headers: { authorization: `Bearer ${accessToken}` },
      };
    } else if (code) {
      requestOptions.queryParams = {
        ...requestOptions.queryParams,
        code,
      };
    }

    return await fetchCommentGetAll({
      ...requestOptions,
    });
  };

  const {
    data,
    isLoading: agentConversationLoading,
    refetch: refetchAgentConversationMessages,
    fetchNextPage: fetchNextAgentConversationPage,
    hasNextPage: hasAgentConversationNextPage,
  } = useInfiniteQuery({
    queryKey: [queryKey, instanceId, stepId],
    queryFn: fetchAgentMessages,
    keepPreviousData: true,
    getNextPageParam: ({ meta }) => {
      if (meta.hasNextPage) {
        return meta.page + 1;
      }
      return false;
    },
    enabled: Boolean(
      chatOpen && instanceId && stepId && chatMode !== ChatMode.Preview,
    ),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const sendMessageToAgent = useCallback(
    async (message: string, file?: RcFile) => {
      if (!instanceId || !stepId) {
        return;
      }

      setIsLoading(true);

      try {
        addComment(
          {
            contentType: OpenapiContentType.FormData,
            pathParams: { instanceId, stepId },
            body: {
              comment: message.trim(),
              file,
              code,
              postedBy,
            },
          },
          {
            onSuccess: () => {
              setIsLoading(false);
              refetchAgentConversationMessages();
            },
            onError: (error) => {
              captureError(error, {
                module: 'agent_chat',
                action: 'send_message',
                metadata: { procId: instanceId },
              });
              setIsLoading(false);
            },
          },
        );
      } catch (error) {
        captureError(error, {
          module: 'agent_chat',
          action: 'send_message',
          metadata: { procId: instanceId },
        });
      }
    },
    [
      addComment,
      code,
      instanceId,
      postedBy,
      refetchAgentConversationMessages,
      setIsLoading,
      stepId,
    ],
  );

  const isMyMessageInAgentConversation = useCallback(
    (createdBy: ChatMessageType, chatMode?: ChatMode) =>
      (createdBy === ChatMessageType.User && chatMode === ChatMode.User) ||
      (createdBy === ChatMessageType.Author && chatMode === ChatMode.Author),
    [],
  );

  return {
    agentConversationMessages: agentConversationHistoryResponseToMessages(data),
    agentConversationLoading,
    hasAgentConversationNextPage,
    fetchNextAgentConversationPage,
    refetchAgentConversationMessages,
    sendMessageToAgent,
    isMyMessageInAgentConversation,
  };
};
